import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import {
  meetingCalendarStatus,
  type ClinicianMeeting,
} from "@care-by-ella/contracts";
import { PatientLabel } from "@/components/PatientLabel";
import { useReadOnly, useViewPath } from "@/components/SupportView";
import {
  cancelClinicianMeeting,
  fetchMedicalEvidence,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  countdownText,
  evidenceText,
  meetingKindLabel,
  whenText,
} from "@/lib/meeting-calendar";
import { BackupPhoneLine } from "@/components/meeting/BackupPhoneLine";
import { missingBasis } from "@/lib/missing-basis";
import { patientHref, patientMeetingHref } from "@/lib/patient-links";
import { MeetingSignals } from "./MeetingSignals";
import { MeetingStatusTag } from "./MeetingStatusTag";

// TES-1233 — Emelie: "Vid klick öppnas en sidopanel utan att
// kalenderöverblicken försvinner. Där visas patientöversikt, möteslänk, tid
// till mötet, hälsodeklaration, medicinska flaggor, saknade underlag och
// relevanta åtgärder."
//
// Not a modal: the calendar stays visible and usable beside it. Focus moves
// to the panel when it opens; Escape or "Stäng" closes it and the caller puts
// focus back on the block that opened it.
export function MeetingSidePanel({
  meeting,
  now,
  onClose,
  onChanged,
  onUnauthorized,
}: {
  meeting: ClinicianMeeting;
  now: number;
  onClose: () => void;
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  const viewPath = useViewPath();
  const readOnly = useReadOnly();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [missing, setMissing] = useState<string[] | null>(null);
  const [missingError, setMissingError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const status = meetingCalendarStatus(meeting, now);
  const evidence = evidenceText(meeting.evidence);
  const flags = meeting.medicalFlagCount ?? 0;

  useEffect(() => {
    headingRef.current?.focus();
  }, [meeting.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The open completion checklist's rows that are not done — the same
  // "saknat underlag" the meeting note shows (lib/missing-basis).
  useEffect(() => {
    let live = true;
    setMissing(null);
    setMissingError(false);
    fetchMedicalEvidence(meeting.patient.id)
      .then((d) => {
        if (live) setMissing(missingBasis(d));
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        if (live) setMissingError(true);
      });
    return () => {
      live = false;
    };
  }, [meeting.patient.id]);

  async function cancel() {
    if (
      !window.confirm(
        `Avboka mötet ${whenText(meeting.scheduledAt, new Date(now))}? Patienten meddelas och kan boka ny tid.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancelClinicianMeeting(meeting.id);
      onChanged();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const meetingLink = viewPath(
    patientMeetingHref(meeting.patient.id, meeting.id),
  );
  const rebook = status === "needs_rebooking" || status === "patient_no_show";

  return (
    <aside
      aria-labelledby="meeting-panel-heading"
      className="flex flex-col gap-md rounded-md border border-surfaceMuted bg-surface p-md shadow-sm"
    >
      <div className="flex items-start justify-between gap-sm">
        <div className="min-w-0">
          <h2
            id="meeting-panel-heading"
            ref={headingRef}
            tabIndex={-1}
            className="font-display text-lg tracking-tight focus:outline-none"
          >
            {whenText(meeting.scheduledAt, new Date(now))}
          </h2>
          <p className="text-sm text-textSecondary">
            {meetingKindLabel(meeting)} · {meeting.durationMinutes} min
            {meeting.doctor ? ` · ${meeting.doctor.displayName}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-xs text-textSecondary hover:text-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <X aria-hidden className="h-5 w-5" />
          <span className="sr-only">Stäng</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-sm">
        <MeetingStatusTag status={status} />
        <span className="text-sm text-textSecondary">
          {countdownText(meeting, now)}
        </span>
      </div>
      <MeetingSignals meeting={meeting} now={now} />
      {/* TES-1306 — beside "Öppna mötet", while the call is still ahead. */}
      {status === "booked" || status === "ongoing" ? (
        <BackupPhoneLine backupPhone={meeting.patient.backupPhone} />
      ) : null}

      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-sm text-sm">
        <dt className="text-textSecondary">Patient</dt>
        <dd className="min-w-0">
          <PatientLabel
            patientId={meeting.patient.id}
            name={meeting.patient.name}
            personalNumberLast6={meeting.patient.personalNumberLast6}
            link
          />
        </dd>
        <dt className="text-textSecondary">Hälsodeklaration</dt>
        <dd>{meeting.healthDeclarationSubmitted ? "Inskickad" : "Saknas"}</dd>
        <dt className="text-textSecondary">Medicinska flaggor</dt>
        <dd>{flags === 0 ? "Inga" : flags}</dd>
        {evidence ? (
          <>
            <dt className="text-textSecondary">Underlag</dt>
            <dd>{evidence.text}</dd>
          </>
        ) : null}
        <dt className="text-textSecondary">Saknade underlag</dt>
        <dd>
          {missingError ? (
            <span className="text-textSecondary">Kunde inte hämtas.</span>
          ) : missing === null ? (
            <span className="text-textSecondary">Hämtar…</span>
          ) : missing.length === 0 ? (
            "Inga"
          ) : (
            <ul className="list-disc pl-md">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </dd>
      </dl>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-sm">
        <Link
          to={meetingLink}
          className="rounded-md bg-brand px-md py-xs text-sm font-medium text-textOnBrand hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          Öppna mötet
        </Link>
        <Link
          to={viewPath(patientHref(meeting.patient.id))}
          className="rounded-md border border-surfaceMuted px-md py-xs text-sm hover:border-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          Patientöversikt
        </Link>
        {rebook ? (
          // The booking lives on the patient overview ("Boka nytt vårdmöte").
          <Link
            to={viewPath(patientHref(meeting.patient.id))}
            className="rounded-md border border-brand px-md py-xs text-sm text-brand-deep hover:bg-brand/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Boka nytt möte
          </Link>
        ) : null}
        {status === "booked" && !readOnly ? (
          <button
            type="button"
            onClick={() => void cancel()}
            disabled={busy}
            className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textSecondary hover:border-danger hover:text-danger disabled:opacity-60"
          >
            Avboka
          </button>
        ) : null}
      </div>
    </aside>
  );
}
